import { ErrorLogService } from "../services/errorLogService";
import { ConversationService, MessageRating } from "../services/conversationService";

// Thumbs up/down on an AI Analyst response.
// - Either rating is persisted on the MCPMessage (so it survives reload and can't be re-filed twice).
// - Only a thumbs-down files an ErrorLog (source CHAT_FEEDBACK) for the admin queue, with the
//   user's prompt, the flagged answer and an optional comment inlined so admins can read it
//   without opening the chat. See docs/architecture/CHAT_FEEDBACK.md.

export const CHAT_FEEDBACK_COMMENT_MAX = 1000;
const SNIPPET_MAX = 600;

const snippet = (text: string | undefined | null, max = SNIPPET_MAX): string => {
  const trimmed = (text ?? "").trim();
  return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
};

export interface ChatFeedbackContext {
  conversationId: string;
  messageId: string;
  // The user prompt that triggered the flagged answer (may be missing for legacy transcripts)
  prompt?: string | null;
  // The flagged AI answer text
  answer?: string | null;
  comment?: string | null;
  userEmail?: string | null;
}

export function buildChatFeedbackDescription({
  prompt,
  answer,
  comment,
}: Pick<ChatFeedbackContext, "prompt" | "answer" | "comment">): string {
  // No header line: the admin list already labels the row with its source badge.
  const lines: string[] = [];
  const trimmedComment = (comment ?? "").trim();
  if (trimmedComment) {
    lines.push(`User comment: ${trimmedComment}`, "");
  }
  lines.push(
    `User prompt: ${snippet(prompt) || "(none)"}`,
    "",
    `AI response: ${snippet(answer) || "(empty)"}`
  );
  return lines.join("\n");
}

// Persist a rating on the message. Throws on failure so the caller can revert its optimistic state.
export async function persistMessageRating(messageId: string, rating: MessageRating): Promise<void> {
  await ConversationService.rateMessage(messageId, rating);
}

// File the thumbs-down with the admin queue. Called once the user has either written a comment
// or explicitly skipped it; a non-empty comment is also stored on the message.
export async function submitChatThumbsDown(ctx: ChatFeedbackContext): Promise<void> {
  const comment = (ctx.comment ?? "").trim().slice(0, CHAT_FEEDBACK_COMMENT_MAX);
  await ErrorLogService.createErrorLog({
    description: buildChatFeedbackDescription({ prompt: ctx.prompt, answer: ctx.answer, comment }),
    source: "CHAT_FEEDBACK",
    mcpConversationId: ctx.conversationId,
    mcpMessageId: ctx.messageId,
    // Origin + path only — never query params or hash (can carry tokens / share links)
    pageUrl: window.location.origin + window.location.pathname,
    userEmail: ctx.userEmail ?? null,
  });
  if (comment) {
    // Best-effort: the comment is already in the ErrorLog description above. Throwing here would
    // make the UI show "try again", and a retry would file a second ErrorLog for the same message.
    try {
      await ConversationService.rateMessage(ctx.messageId, "DOWN", comment);
    } catch (err) {
      console.error("Chat feedback filed, but saving the comment on the message failed:", err);
    }
  }
}
